/**
 * Cost Breakdown Section Component
 * Inline editable cost breakdown with total calculation
 * Order: Labour, Materials, Plant, Subcontractors, Prelims, Pro Sums, O&P
 */
import { useState } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Edit, Save, X, Loader2 } from 'lucide-react';
import { formatCurrency } from './constants';

export function CostBreakdownSection({ 
  tender, 
  canEdit, 
  isArchived, 
  onSave,
  submitting 
}) {
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  const startEdit = () => {
    setEditData({
      estimated_labour: tender.estimated_labour || '',
      estimated_materials: tender.estimated_materials || '',
      estimated_plant: tender.estimated_plant || '',
      estimated_subcontractors: tender.estimated_subcontractors || '',
      estimated_prelims: tender.estimated_prelims || '',
      estimated_prosums: tender.estimated_prosums || '',
      estimated_op: tender.estimated_op || ''
    });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditData({});
  };

  const handleSave = async () => {
    const data = {
      estimated_labour: parseFloat(editData.estimated_labour) || null,
      estimated_materials: parseFloat(editData.estimated_materials) || null,
      estimated_plant: parseFloat(editData.estimated_plant) || null,
      estimated_subcontractors: parseFloat(editData.estimated_subcontractors) || null,
      estimated_prelims: parseFloat(editData.estimated_prelims) || null,
      estimated_prosums: parseFloat(editData.estimated_prosums) || null,
      estimated_op: parseFloat(editData.estimated_op) || null
    };
    
    const success = await onSave(data);
    if (success) {
      setEditMode(false);
      setEditData({});
    }
  };

  const calculateTotal = () => {
    if (editMode) {
      return (
        (parseFloat(editData.estimated_labour) || 0) +
        (parseFloat(editData.estimated_materials) || 0) +
        (parseFloat(editData.estimated_plant) || 0) +
        (parseFloat(editData.estimated_subcontractors) || 0) +
        (parseFloat(editData.estimated_prelims) || 0) +
        (parseFloat(editData.estimated_prosums) || 0) +
        (parseFloat(editData.estimated_op) || 0)
      );
    }
    return (
      (tender.estimated_labour || 0) +
      (tender.estimated_materials || 0) +
      (tender.estimated_plant || 0) +
      (tender.estimated_subcontractors || 0) +
      (tender.estimated_prelims || 0) +
      (tender.estimated_prosums || 0) +
      (tender.estimated_op || 0)
    );
  };

  // Cost breakdown fields in correct order
  const costFields = [
    { key: 'estimated_labour', label: 'Labour' },
    { key: 'estimated_materials', label: 'Materials' },
    { key: 'estimated_plant', label: 'Plant' },
    { key: 'estimated_subcontractors', label: 'Subcontractors' },
    { key: 'estimated_prelims', label: 'Prelims' },
    { key: 'estimated_prosums', label: 'Pro Sums' },
    { key: 'estimated_op', label: 'O&P' }
  ];

  return (
    <div className="pt-3 border-t border-white/10">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-gray-500 text-xs uppercase">Cost Breakdown</Label>
        {!isArchived && canEdit && !editMode && (
          <Button
            variant="ghost"
            size="sm"
            onClick={startEdit}
            className="text-gray-400 hover:text-[#F5A623] h-6 px-2"
            data-testid="edit-costs-btn"
          >
            <Edit className="w-3 h-3 mr-1" />
            Edit
          </Button>
        )}
        {editMode && (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={cancelEdit}
              className="text-gray-400 hover:text-white h-6 px-2"
            >
              <X className="w-3 h-3 mr-1" />
              Cancel
            </Button>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={submitting}
              className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] h-6 px-2"
              data-testid="save-costs-btn"
            >
              {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Save className="w-3 h-3 mr-1" />Save</>}
            </Button>
          </div>
        )}
      </div>

      {editMode ? (
        <div className="grid grid-cols-2 gap-3">
          {costFields.map(field => (
            <div key={field.key} className="space-y-1">
              <Label className="text-gray-500 text-xs">{field.label} (£)</Label>
              <Input
                type="number"
                value={editData[field.key] || ''}
                onChange={(e) => setEditData(prev => ({ ...prev, [field.key]: e.target.value }))}
                className="bg-[#1E2530] border-white/20 text-white h-8"
                placeholder="0"
              />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          {costFields.map(field => (
            <div key={field.key}>
              <Label className="text-gray-500 text-xs">{field.label}</Label>
              <p className="text-white">{formatCurrency(tender[field.key])}</p>
            </div>
          ))}
        </div>
      )}
      
      <div className="mt-3 pt-2 border-t border-white/5">
        <Label className="text-gray-500 text-xs uppercase">Contract Value</Label>
        <p className="text-xl font-bold text-[#F5A623]">
          {formatCurrency(calculateTotal())}
        </p>
      </div>
    </div>
  );
}
